/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { isEmpty } from 'modules/common/helpers/object';
import { useEffect, useState } from 'react';
import { ENVIRONMENT } from 'config';
/**
 * Power BI visual component
 * @param {String} cssClassName - Class name for the visual
 * @param {String} accessToken - Power bi access token
 * @param {String} embedUrl - Power bi embed url
 * @param {String} id - Power bi report id
 * @param {String} pageName - Power bi page name
 * @param {Array} filters - List of filters to be applied for the visual
 * @param {Function} setPBIReport - Function to set loaded report object
 * @param {Function} setIsLoaded - Function to set report loaded state
 * @returns
 */

const VisualCard = (props) => {
  const {
    cssClassName,
    accessToken,
    embedUrl,
    visualName,
    id,
    pageName,
    filters,
    reportName,
    pageDisplayName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
  } = props;
  const [reportInstance, setReportInstance] = useState();
  let visualRenderedCount = new Map();

  const handleLogging = (message) => {
    if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === 'true') {
      console.log(message);
    }
  };
  // Triggered when the filters value changes
  useEffect(() => {
    // Update visual filters on filter change
    if (reportInstance && filters) {
      reportInstance?.updateFilters(models.FiltersOperations.ReplaceAll, filters).catch((error) => {
        // eslint-disable-next-line
        console.error(error);
      });
    }
  }, [filters, reportInstance]);
  //
  useEffect(() => {
    // update the timestamp in the localstorage to calculate the time taken to complete the visual render on each rendered event
    localStorage.setItem('visualTimeDifference', JSON.stringify(new Date().toISOString()));
  }, [pageDisplayName]);
  //
  return (
    <PowerBIEmbed
      embedConfig={{
        type: 'report',
        tokenType: models.TokenType.Embed,
        accessToken,
        embedUrl,
        id,
        pageName,
        ...(isEmpty(reportInstance?.config?.filter) && { filters }),
        settings: {
          displayOption: models.DisplayOption.FitToPage,
          visualRenderedEvents: true,
          panes: {
            filters: {
              expanded: false,
              visible: false,
            },
            pageNavigation: {
              visible: false,
            },
          },
          background: models.BackgroundType.Transparent,
        },
      }}
      cssClassName={cssClassName}
      eventHandlers={
        new Map([
          [
            'error',
            (event) => {
              const logMessage = `${new Date().toISOString()} - Event : error | Visual : ${visualName} | Report : ${reportName} | Page : ${pageDisplayName} | Message : ${
                event?.detail?.detailedMessage
              }`;
              handleLogging(logMessage, 'error');
            },
          ],
          [
            'loaded',
            function loaded() {
              const logMessage = `${new Date().toISOString()} - Event : loaded | Visual : ${visualName} | Report : ${reportName} | Page : ${pageDisplayName}`;
              handleLogging(logMessage, 'info', 'loaded');
              setIsLoaded(true);
            },
          ],
          [
            'rendered',
            (event) => {
              const currentData = new Date();
              //
              const dispatchDuration = NaN;
              //
              const logMessage = `${currentData.toISOString()} - Event : rendered | Visual : ${visualName} | Report : ${reportName} | Page : ${pageDisplayName} | Visual Rendered Count : ${visualRenderedCount.get(
                `${visualName}_${reportName}_${pageDisplayName}`
              )} | Duration between filter dispatch : ${dispatchDuration}`;
              //
              handleLogging(logMessage, 'info', 'rendered');
              if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === 'true') {
                visualRenderedCount = new Map();
              }
              if (event.target.classList.contains('loader')) {
                event.target.classList.remove('loader');
              }
            },
          ],
          [
            'visualRendered',
            (event) => {
              const logMessage = `${new Date().toISOString()} - Event : visualRendered | Visual : ${visualName} | Report : ${reportName} | Page : ${pageDisplayName}`;
              handleLogging(logMessage, 'info', 'visualRendered');
              if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === 'true') {
                visualRenderedCount.set(
                  `${visualName}_${reportName}_${pageDisplayName}`,
                  visualRenderedCount.get(`${visualName}_${reportName}_${pageDisplayName}`)
                    ? visualRenderedCount.get(`${visualName}_${reportName}_${pageDisplayName}`) + 1
                    : 1
                );
              }
              if (!event.target.classList.contains('loader')) {
                event.target.classList.add('loader');
              }
            },
          ],
        ])
      }
      getEmbeddedComponent={(embeddedReport) => {
        setPBIReport(embeddedReport);
        setReportInstance(embeddedReport);
      }}
    />
  );
};
//
export default VisualCard;
